import { QuoteUnderwritingTaskSearchResponse } from '@lio/api-types';

import { AlertStatus } from '@models/alert';
import { MultiPolicyQuoteStatus, QuoteStatus, QuoteType, TQuoteApiResponse } from '@models/quotes';
import { IMenuItemExtended, MenuItemType } from '@components/menu';

import { IDialogState } from '@pages/QuoteDetails/types';
import { UserStoreSlice } from '@store/types/UserStoreSlice.type';
import { InsuranceProductIds } from '@models/newQuote';
import { writingCompanyIds } from '@models/productOffering';

export enum Action {
  AcceptQuote = 'AcceptQuote',
  ReleaseQuote = 'ReleaseQuote',
  RemoveBrokerAccess = 'RemoveBrokerAccess',
  ManageRoles = 'ManageRoles',
  GrantCustomerAccess = 'GrantCustomerAccess',
  ResetQuoteDefaults = 'ResetQuoteDefaults',
  CancelQuote = 'CancelQuote',
  DeclineQuote = 'DeclineQuote',
  GenerateConditionalRenewal = 'GenerateConditionalRenewal',
  MarkForNonrenewal = 'MarkForNonrenewal',
  RateChangeAnalysis = 'RateChangeAnalysis',
  DownloadApplication = 'DownloadApplication',
  ViewExternalSentEmails = 'ViewExternalSentEmails',
  ViewQuoteDeclarations = 'ViewQuoteDeclarations',
  ReactivateDeclinedRejectedQuote = 'ReactivateDeclinedRejectedQuote',
  ReactivateQuote = 'reactivateQuote',
  ExitQuote = 'exitQuote',
  ReviewUnderwritingIssues = 'reviewUnderwritingIssues',
  AddNote = 'addNote',
  MessageBroker = 'messageBroker',
  SaveChanges = 'saveChanges',
  OpenChangeSummary = 'openChangeSummary',
  ToggleLockQuote = 'toggleLockQuote',
  ViewIneligibilities = 'ViewIneligibilities',
  CopyQuote = 'CopyQuote',
  SwitchOffering = 'SwitchOffering',
  ReassignCustomer = 'ReassignCustomer',
  ViewDuplicateQuotes = 'viewDuplicateQuotes',
  ConvertRenewalToNewBusiness = 'ConvertRenewalToNewBusiness',
  ApplyIssuingCompanyOverride = 'ApplyIssuingCompanyOverride',
  RemoveIssuingCompanyOverride = 'RemoveIssuingCompanyOverride',

  // admin only actions
  ReleaseQuoteToPolicyHolder = 'releaseQuoteToPolicyHolder',
  MarkAsReferredToBroker = 'markAsReferredToBroker',
  GenerateDocument = 'generateDocument',
}

export type TQuoteDetailDetailFunctionBody = {
  /** Quote api response */
  quote: TQuoteApiResponse;
  /** Underwriting task api response */
  underwritingTasks?: QuoteUnderwritingTaskSearchResponse;
  /** Information about the logged in user's roles */
  userAuth: UserStoreSlice['userAuth'];
  /** Indicates whether this policy already has an active Conditional Renewal transaction */
  hasConditionalRenewalTransaction?: boolean;
  /** Indicates whether related policy is currently marked for nonrenewal */
  isPolicyMarkedForNonrenewal?: boolean;
};

export const activeQuoteStatuses = [
  QuoteStatus.Bound.queryParam,
  QuoteStatus.ReadyToBind.queryParam,
  QuoteStatus.Rated.queryParam,
  QuoteStatus.Submission.queryParam,
  QuoteStatus.Referred.queryParam,
  QuoteStatus.Underwritten.queryParam,
];

const closedQuoteStatuses = [
  QuoteStatus.Canceled.name,
  QuoteStatus.Declined.name,
  QuoteStatus.Expired.name,
  QuoteStatus.Rejected.name,
];

export const disabledQuotePanelStatuses = [...closedQuoteStatuses, QuoteStatus.Bound.name];
export const alertQuoteStatuses = [...disabledQuotePanelStatuses, QuoteStatus.Referred.name];

export const readOnlyQuoteStatuses = [
  QuoteStatus.Bound.id,
  QuoteStatus.Canceled.id,
  QuoteStatus.Declined.id,
  QuoteStatus.Expired.id,
  QuoteStatus.Rejected.id,
  QuoteStatus.Referred.id,
];

export const MultiPolicyQuoteInternalAlertInfo = {
  [MultiPolicyQuoteStatus.QuoteBoundRelatedQuoteFailedBind]: {
    message: 'This quote is bound, but a related quote failed to bind. Please contact LIO Support for assistance.',
    severity: AlertStatus.Warning,
  },
  [MultiPolicyQuoteStatus.QuoteFailedBindRelatedQuoteFailedBind]: {
    message: 'This quote has a related quote that failed to bind. Please contact LIO Support for assistance.',
    severity: AlertStatus.Warning,
  },
};

export const MultiPolicyQuoteExternalAlertInfo = {
  [MultiPolicyQuoteStatus.QuoteBoundRelatedQuoteFailedBind]: {
    message: 'This quote is bound, but a related quote failed to bind. Please contact your underwriter for assistance.',
    severity: AlertStatus.Warning,
  },
  [MultiPolicyQuoteStatus.QuoteFailedBindRelatedQuoteFailedBind]: {
    message: 'This quote has a related quote that failed to bind. Please contact your underwriter for assistance.',
    severity: AlertStatus.Warning,
    ctaText: 'Send Message',
    ctaUrl: '?sidebarItem=messages',
  },
};

export const QuoteStatusAlertInfo = {
  [QuoteStatus.Referred.name]: {
    message: 'This quote has been referred to underwriting.',
    severity: AlertStatus.Warning,
    ctaText: 'View Reasons',
    ctaUrl: '?sidebarItem=underwriting&sidebarTab=underwriting-issues',
  },
  [QuoteStatus.Bound.name]: {
    message: 'This quote has been bound.',
    severity: AlertStatus.Promotion,
    ctaText: 'Go to Policy',
  },
  [QuoteStatus.Declined.name]: {
    message: 'This quote has been reviewed and declined by the LIO Underwriting Team.',
    severity: AlertStatus.Incident,
  },
  [QuoteStatus.Canceled.name]: {
    message: 'This quote has been cancelled but can be reactivated.',
    severity: AlertStatus.Incident,
    ctaText: 'Reactivate Quote',
    ctaFunction: () => {},
  },
  [QuoteStatus.Expired.name]: {
    message: 'This quote has lapsed but can be reactivated.',
    severity: AlertStatus.Incident,
    ctaText: 'Reactivate Quote',
    ctaFunction: () => {},
  },
  [QuoteStatus.Rejected.name]: {
    message:
      'This quote has been automatically rejected due to ineligible factors and/or criteria. Please contact your underwriter with questions.',
    severity: AlertStatus.Incident,
  },
};

export const QuoteReadOnlyAlertInfo = {
  [QuoteStatus.Referred.name]: {
    message:
      'This quote has been referred to the LIO Underwriting Team for review. If you have questions, please contact your underwriter.',
    severity: AlertStatus.Warning,
    ctaText: 'Send Message',
    ctaUrl: '?viewReadOnly=true&sidebarItem=messages',
  },
  [QuoteStatus.ReadyToBind.queryParam]: {
    message:
      'This quote has been referred to the LIO Underwriting Team for review. If you have questions, please contact your underwriter.',
    severity: AlertStatus.Warning,
    ctaText: 'Send Message',
    ctaUrl: '?viewReadOnly=true&sidebarItem=messages',
  },
  [QuoteStatus.Rated.name]: {
    message:
      'This quote has been referred to the LIO Underwriting Team for review. If you have questions, please contact your underwriter.',
    severity: AlertStatus.Warning,
    ctaText: 'Send Message',
    ctaUrl: '?viewReadOnly=true&sidebarItem=messages',
  },
  [QuoteStatus.Bound.name]: {
    message: 'This quote has been bound.',
    severity: AlertStatus.Promotion,
    ctaText: 'Go to Policy',
  },
  [QuoteStatus.Canceled.name]: {
    message: 'This quote has been cancelled.',
    severity: AlertStatus.Incident,
    ctaText: 'Send Message',
    ctaUrl: '?viewReadOnly=true&sidebarItem=messages',
  },
  [QuoteStatus.Declined.name]: {
    message: 'This quote has been reviewed and declined by the LIO Underwriting Team.',
    severity: AlertStatus.Incident,
    ctaText: 'Send Message',
    ctaUrl: '?viewReadOnly=true&sidebarItem=messages',
  },
  [QuoteStatus.Expired.name]: {
    message: 'This quote has lapsed because more than 7 days have passed since the specified effective date.',
    severity: AlertStatus.Incident,
  },
  [QuoteStatus.Rejected.name]: {
    message:
      'This quote has been automatically rejected due to ineligible factors and/or criteria. Please contact your underwriter with questions.',
    severity: AlertStatus.Incident,
  },
};

export const underlyingCoverageBindingRequirementDefaultMessage =
  'This quote cannot be bound until its underlying General Liability coverage is bound.';

export const isUnderlyingCoverageBindingRequirementUnmet = (
  underlyingCoverageBindingRequirement: TQuoteApiResponse['underlyingCoverageBindingRequirement'],
): underlyingCoverageBindingRequirement is NonNullable<TQuoteApiResponse['underlyingCoverageBindingRequirement']> =>
  !!underlyingCoverageBindingRequirement?.required && !underlyingCoverageBindingRequirement.satisfied;

export const terminalStatuses = [
  QuoteStatus.Bound.name,
  QuoteStatus.Rejected.name,
  QuoteStatus.Declined.name,
  QuoteStatus.Canceled.name,
  QuoteStatus.Expired.name,
];

export const initialActionItems: IMenuItemExtended<TQuoteDetailDetailFunctionBody>[] = [
  {
    type: MenuItemType.Default,
    text: 'Open change summary',
    actionName: Action.OpenChangeSummary,
    isShown: ({ quote, userAuth }) =>
      quote.quoteType === QuoteType.Endorsement || (quote.quoteType === QuoteType.Renewal && userAuth.isInternalUser),
  },
  {
    type: MenuItemType.Default,
    text: 'Release quote to Broker',
    actionName: Action.ReleaseQuote,
    isShown: ({ quote, userAuth }) => {
      return (
        Boolean(quote.hasInternalOnlyAccessRestriction) &&
        (userAuth.isUnderwriter || userAuth.isPortalAdmin || userAuth.isAdmin || userAuth.isLIOContractor) &&
        (quote.quoteType === 'Renewal' || quote.quoteType === 'NewBusiness')
      );
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Remove Broker access',
    actionName: Action.RemoveBrokerAccess,
    isShown: ({ quote, userAuth }) => {
      return (
        !quote.hasInternalOnlyAccessRestriction &&
        (userAuth.isUnderwriter ||
          userAuth.isPortalAdmin ||
          userAuth.isAdmin ||
          userAuth.isSrOps ||
          userAuth.isLIOContractor) &&
        (quote.quoteType === 'Renewal' || quote.quoteType === 'NewBusiness')
      );
    },
  },
  // release to policyholder flag
  {
    type: MenuItemType.Default,
    text: 'Turn on RTP flag',
    actionName: Action.ReleaseQuoteToPolicyHolder,
    isShown: ({ quote, userAuth }) => {
      return !quote?.releasedToPolicyHolder && (userAuth.isAdmin || userAuth.isPortalAdmin);
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Turn off RTP flag',
    actionName: Action.ReleaseQuoteToPolicyHolder,
    isShown: ({ quote, userAuth }) => {
      return !!quote?.releasedToPolicyHolder && (userAuth.isAdmin || userAuth.isPortalAdmin);
    },
  },
  // end release to policyholder flag

  // referred to broker flag
  {
    type: MenuItemType.Default,
    text: 'Turn on RTB flag',
    actionName: Action.MarkAsReferredToBroker,
    isShown: ({ quote, userAuth }) => {
      return !quote.referredToBroker && (userAuth.isAdmin || userAuth.isPortalAdmin);
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Turn off RTB flag',
    actionName: Action.MarkAsReferredToBroker,
    isShown: ({ quote, userAuth }) => {
      return !!quote.referredToBroker && (userAuth.isAdmin || userAuth.isPortalAdmin);
    },
  },
  // end referred to broker flag
  {
    type: MenuItemType.Default,
    text: 'Accept quote',
    actionName: Action.AcceptQuote,
    isShown: ({ userAuth }) => userAuth.isUnderwriter || userAuth.isPortalAdmin || userAuth.isLIOContractor,
    isDisabled: ({ quote, underwritingTasks }) =>
      quote?.status.name !== QuoteStatus.Referred.name ||
      (underwritingTasks?.items &&
        underwritingTasks?.items.length !== 0 &&
        underwritingTasks.items.some((el) => el.status !== 'Accepted')) ||
      false,
  },
  {
    type: MenuItemType.Default,
    text: 'Decline quote',
    actionName: Action.DeclineQuote,
    isShown: ({ userAuth }) => userAuth.isInternalUser && !userAuth.isLioSales,
    isDisabled: ({ quote }) => terminalStatuses.includes(quote?.status.name),
  },
  {
    type: MenuItemType.Default,
    text: 'Generate Conditional Renewal',
    actionName: Action.GenerateConditionalRenewal,
    isShown: ({ quote, userAuth }) =>
      quote.quoteType === QuoteType.Renewal && userAuth.isInternalUser && !userAuth.isLioSales,
    isDisabled: ({ quote, hasConditionalRenewalTransaction }) =>
      terminalStatuses.includes(quote?.status.name) || Boolean(hasConditionalRenewalTransaction),
  },
  {
    type: MenuItemType.Default,
    text: 'Mark for Nonrenewal',
    actionName: Action.MarkForNonrenewal,
    isShown: ({ quote, userAuth }) =>
      quote.quoteType === QuoteType.Renewal && userAuth.isInternalUser && !userAuth.isLioSales,
    isDisabled: ({ quote }) => terminalStatuses.includes(quote?.status.name),
  },
  {
    type: MenuItemType.Default,
    text: 'Reactivate quote',
    actionName: Action.ReactivateDeclinedRejectedQuote,
    isShown: ({ quote, userAuth }) =>
      !userAuth.isLioSales && [QuoteStatus.Declined.name, QuoteStatus.Rejected.name].includes(quote?.status.name), // BOR failure/UW decline reactivation
    isDisabled: ({ quote, isPolicyMarkedForNonrenewal }) =>
      quote.quoteType === QuoteType.Renewal && Boolean(isPolicyMarkedForNonrenewal),
  },
  {
    type: MenuItemType.Default,
    text: 'Reactivate quote',
    actionName: Action.ReactivateQuote,
    isShown: ({ quote, userAuth }) =>
      !userAuth.isLioSales && ![QuoteStatus.Declined.name, QuoteStatus.Rejected.name].includes(quote?.status.name), // "normal" reactivation
    isDisabled: ({ quote, isPolicyMarkedForNonrenewal }) => {
      const isTerminalStatus = [QuoteStatus.Expired.name, QuoteStatus.Canceled.name].includes(quote?.status.name);

      const isCanceledDueToNonrenewal =
        quote.quoteType === QuoteType.Renewal &&
        quote.status.name === QuoteStatus.Canceled.name &&
        Boolean(isPolicyMarkedForNonrenewal);

      return !isTerminalStatus || isCanceledDueToNonrenewal;
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Cancel quote',
    actionName: Action.CancelQuote,
    isShown: ({ quote, userAuth }) => !(quote.quoteType === QuoteType.Endorsement || userAuth.isLioSales),
    isDisabled: ({ quote }) => terminalStatuses.includes(quote?.status.name),
  },
  {
    type: MenuItemType.Default,
    text: 'Convert to New Business',
    actionName: Action.ConvertRenewalToNewBusiness,
    isShown: ({ quote, userAuth }) => quote.quoteType === QuoteType.Renewal && userAuth.isInternalWithoutSales,
    isDisabled: ({ quote }) => terminalStatuses.includes(quote?.status.name),
  },
  {
    type: MenuItemType.Default,
    text: 'View Ineligibilities',
    actionName: Action.ViewIneligibilities,
    disabled: false,
    isShown: ({ quote, userAuth }) => {
      if (!userAuth.isInternalUser) {
        return false;
      }
      return quote?.status.name === QuoteStatus.Rejected.name;
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Refer to colleague',
    hidden: true,
    action: () => {},
  },
  {
    type: MenuItemType.Divider,
    isShown: ({ userAuth }) => !userAuth.isLioSales,
  },
  {
    type: MenuItemType.Default,
    text: 'Review underwriting issues',
    actionName: Action.ReviewUnderwritingIssues,
    isShown: ({ userAuth }) => !userAuth.isLioSales,
  },
  {
    type: MenuItemType.Default,
    text: 'Add note',
    actionName: Action.AddNote,
  },
  {
    type: MenuItemType.Default,
    text: 'Message broker',
    actionName: Action.MessageBroker,
    isShown: ({ quote, userAuth }) => !(quote.quoteType === QuoteType.Endorsement) && !userAuth.isLioSales,
  },
  {
    type: MenuItemType.Divider,
    isShown: ({ quote }) => !(quote.quoteType === QuoteType.Endorsement),
  },
  {
    type: MenuItemType.Default,
    text: 'Reset quote defaults',
    actionName: Action.ResetQuoteDefaults,
    isShown: ({ userAuth, quote }) => userAuth.isInternalUser && !terminalStatuses.includes(quote.status.name),
  },
  {
    type: MenuItemType.Default,
    text: 'Manage roles',
    actionName: Action.ManageRoles,
    isShown: ({ quote, userAuth }) => {
      if (quote.quoteType === QuoteType.Endorsement || userAuth.isLioSales) {
        return false;
      }
      return true;
    },
    isDisabled: ({ quote }) => terminalStatuses.includes(quote.status.name),
  },
  {
    type: MenuItemType.Default,
    text: 'Grant Customer Access',
    actionName: Action.GrantCustomerAccess,
    isShown: ({ quote, userAuth }) => {
      const isSwimClubOffering = quote?.offering.insuranceProduct.id === InsuranceProductIds.SwimClub;
      const isSpecialEvent = quote?.offering.insuranceProduct.id === InsuranceProductIds.SpecialEvents;
      const isNotSwimOrSpecialEvent = !isSwimClubOffering && !isSpecialEvent;

      if (isNotSwimOrSpecialEvent || quote.quoteType !== QuoteType.NewBusiness || !userAuth.isInternalUser) {
        return false;
      }

      return true;
    },
    isDisabled: ({ quote }) => terminalStatuses.includes(quote.status.name),
  },
  {
    type: MenuItemType.Default,
    text: 'Rate Change Analysis',
    actionName: Action.RateChangeAnalysis,
    isShown: ({ userAuth }) => {
      // Only show for LIO Internal users
      return userAuth.isInternalUser;
    },
    isDisabled: ({ quote }) => {
      // note name vs queryParam is odd, but quote.status.name is 'ReadyToBind' not 'Ready To Bind' - that would be quote.status.label
      const isEnabled =
        !!quote?.appliedQuoteRating &&
        [
          QuoteStatus.ReadyForRating.queryParam,
          QuoteStatus.Rated.queryParam,
          QuoteStatus.ReadyToBind.queryParam,
          QuoteStatus.Referred.queryParam,
          QuoteStatus.Underwritten.queryParam,
        ].includes(quote.status.name);

      return !isEnabled;
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Download Application',
    actionName: Action.DownloadApplication,
    isShown: ({ userAuth }) => userAuth.isInternalUser,
  },
  {
    type: MenuItemType.Default,
    text: 'View External Sent Emails',
    actionName: Action.ViewExternalSentEmails,
    isShown: ({ userAuth }) => userAuth.isInternalUser,
  },
  {
    type: MenuItemType.Default,
    text: 'View Quote Declarations',
    actionName: Action.ViewQuoteDeclarations,
    isShown: ({ userAuth }) => userAuth.isInternalUser,
  },
  {
    type: MenuItemType.Default,
    text: 'Generate Document',
    actionName: Action.GenerateDocument,
    isShown: ({ quote, userAuth }) =>
      (userAuth.isAdmin || userAuth.isPortalAdmin) && quote.status.name === QuoteStatus.Bound.name,
  },
  {
    type: MenuItemType.Default,
    text: 'Lock quote',
    actionName: Action.ToggleLockQuote,

    isShown: ({ quote, userAuth }) =>
      (userAuth.isUnderwriter || userAuth.isLIOContractor) && !quote.hasUnderwritingLock,
    isDisabled: ({ quote }) => terminalStatuses.includes(quote?.status.name),
  },
  {
    type: MenuItemType.Default,
    text: 'Unlock quote',
    actionName: Action.ToggleLockQuote,

    isShown: ({ quote, userAuth }) =>
      (userAuth.isUnderwriter || userAuth.isLIOContractor) && Boolean(quote.hasUnderwritingLock),
  },
  {
    type: MenuItemType.Default,
    text: 'Copy quote',
    actionName: Action.CopyQuote,
    isShown: ({ quote, userAuth }) => {
      if (quote.quoteType === QuoteType.Renewal) return false;
      const { isUnderwriter, isAdmin, isPortalAdmin, isJrOps, isSrOps, isBroker, isCSR } = userAuth;
      if (isCSR || isBroker) {
        return [
          QuoteStatus.Rejected.name,
          QuoteStatus.Expired.name,
          QuoteStatus.Canceled.name,
          QuoteStatus.Declined.name,
        ].includes(quote?.status.name);
      }
      return isUnderwriter || isAdmin || isPortalAdmin || isJrOps || isSrOps || isBroker;
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Switch Offering',
    actionName: Action.SwitchOffering,
    isShown: ({ quote, userAuth }) => {
      const isSupportedQuoteType = [QuoteType.NewBusiness, QuoteType.Renewal, QuoteType.Rewrite].includes(
        quote.quoteType as QuoteType,
      );
      return userAuth.isInternalUser && !userAuth.isLioSales && isSupportedQuoteType;
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Reassign Customer',
    actionName: Action.ReassignCustomer,
    // Internal-only, and only for unbound New Business quotes (bound quotes are out of scope).
    isShown: ({ quote, userAuth }) =>
      userAuth.isInternalUser &&
      !userAuth.isLioSales &&
      quote.quoteType === QuoteType.NewBusiness &&
      quote.status.name !== QuoteStatus.Bound.name,
  },
  {
    type: MenuItemType.Default,
    text: 'Issue on LSIC Paper',
    actionName: Action.ApplyIssuingCompanyOverride,
    // Underwriters with canOverrideIssuingCompany may switch an LIC quote to issue on LSIC
    // paper, provided it's still pre-bind and no override is already applied. Rating,
    // coverages, and form selection are unaffected — only carrier identity changes.
    isShown: ({ quote, userAuth }) =>
      userAuth.isUnderwriter &&
      userAuth.canOverrideIssuingCompany &&
      quote.writingCompany?.id === writingCompanyIds.admitted &&
      !quote.issuingWritingCompany?.id,
    isDisabled: ({ quote }) => terminalStatuses.includes(quote?.status.name),
  },
  {
    type: MenuItemType.Default,
    text: 'Remove Issuing Company Override',
    actionName: Action.RemoveIssuingCompanyOverride,
    isShown: ({ quote, userAuth }) =>
      userAuth.isUnderwriter && userAuth.canOverrideIssuingCompany && !!quote.issuingWritingCompany?.id,
    isDisabled: ({ quote }) => terminalStatuses.includes(quote?.status.name),
  },
  {
    type: MenuItemType.Divider,
    isShown: ({ userAuth }) => {
      return !userAuth.isLioSales;
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Save changes',
    actionName: Action.SaveChanges,
    isShown: ({ userAuth }) => !userAuth.isLioSales,
    isDisabled: ({ quote }) => terminalStatuses.includes(quote.status.name),
  },
  {
    type: MenuItemType.Default,
    text: 'Exit quote',
    actionName: Action.ExitQuote,
  },
];

export const initialDialogState: IDialogState = {
  showChangelogDialog: false,
  actionDialog: {
    show: false,
    type: null,
  },
  cancelQuoteDetails: {
    cancelQuoteId: '',
    cancelQuotePolicyNumber: '',
  },
  declineQuoteDetails: {
    declineQuoteId: '',
    declineQuotePolicyNumber: '',
    declineQuoteStatus: '',
    declineQuoteCustomerId: '',
    declineQuoteBrokerageId: '',
    hasInternalOnlyAccessRestriction: false,
  },
  declinedRejectedQuoteDetails: {
    declinedRejectedQuoteId: '',
    declinedRejectedQuotePolicyNumber: '',
    declinedRejectedQuoteStatus: '',
  },
  isQuoteIneligibilitiesOpen: false,
  isRateChangeAnalysisOpen: false,
  confirmDialog: {
    show: false,
    description: '',
    confirmText: '',
    cancelText: '',
    navigatePath: '/',
  },
};
